/**
 * 稿件管理业务层 — 对接 Apifox scj 稿件管理 Controller（文档 12）
 */

import { draftService } from '@/services/artApi';
import {
  unwrapApiList,
  unwrapApiResult,
  mapDraftDtoToPlatform,
  mapDraftPageLayerDto,
  mapBatchGroupDto,
} from '@/utils/artMapper';
import { resolveShopId } from '@/utils/materialApiContext';

/** draftStatus：0 草稿，1 已定稿 */
export const DRAFT_STATUS = {
  DRAFT: 0,
  FINALIZED: 1,
};

function toJsonParam(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export async function fetchDraftList(storeId, user, query = {}) {
  const payload = await draftService.list({
    page: query.page || 1,
    pageSize: query.pageSize || 200,
    designName: query.designName || undefined,
    taskId: query.taskId != null ? Number(query.taskId) : undefined,
    draftStatus: query.draftStatus != null ? Number(query.draftStatus) : undefined,
  });
  const list = unwrapApiList(payload).map(dto => mapDraftDtoToPlatform(dto, resolveShopId(storeId, user)));
  return {
    list,
    pageNum: payload.pageNum ?? query.page ?? 1,
    pageSize: payload.pageSize ?? query.pageSize ?? list.length,
    totalElements: payload.totalElements ?? list.length,
    pages: payload.pages ?? 1,
  };
}

export async function syncDraftLibrary(draftsRef, storeId, user) {
  const storeIds = storeId === 'all'
    ? (user?.stores?.length ? user.stores : ['s1'])
    : [storeId || 's1'];

  const chunks = await Promise.all(
    storeIds.map(sid => fetchDraftList(sid, user, { page: 1, pageSize: 200 })),
  );
  const merged = [];
  const seen = new Set();
  chunks.forEach(({ list }) => {
    list.forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      merged.push(item);
    });
  });
  draftsRef.value = merged;
  return merged;
}

export async function createDraftRemote(storeId, user, input = {}) {
  const shopId = Number(resolveShopId(storeId, user));
  const body = {
    designName: input.designName || input.name || '未命名设计',
    designType: input.designType ?? (input.taskId ? 1 : 0),
    taskId: input.taskId != null ? Number(input.taskId) : undefined,
    templateId: input.templateId != null && !String(input.templateId).startsWith('t_')
      ? Number(input.templateId)
      : undefined,
    shopId,
  };
  const payload = await draftService.create(body);
  return mapDraftDtoToPlatform(unwrapApiResult(payload) || {}, shopId);
}

export async function getDraftDetailRemote(id) {
  const payload = await draftService.getDetail(Number(id) || id);
  return mapDraftDtoToPlatform(unwrapApiResult(payload) || {});
}

export async function renameDraftRemote(id, name) {
  await draftService.rename({ id: Number(id) || id, name });
}

export async function deleteDraftRemote(id) {
  await draftService.remove({ id: Number(id) || id });
}

export async function finalizeDraftRemote(id) {
  await draftService.finalize({ id: Number(id) || id });
}

export async function fetchDraftPageLayers(draftId) {
  const payload = await draftService.listPageLayers(Number(draftId) || draftId);
  return unwrapApiList(payload).map(mapDraftPageLayerDto);
}

export async function updateDraftLayerContent(layerId, layerContentJson) {
  await draftService.updateLayerContent({
    id: Number(layerId) || layerId,
    layerContentJson: toJsonParam(layerContentJson),
  });
}

export async function updateDraftBagPull(layerId, bagPullJson) {
  await draftService.updateBagPull({
    id: Number(layerId) || layerId,
    bagPullJson: toJsonParam(bagPullJson),
  });
}

export async function updateDraftSyncConfig(layerId, syncConfigJson) {
  await draftService.updateSyncConfig({
    id: Number(layerId) || layerId,
    syncConfigJson: toJsonParam(syncConfigJson),
  });
}

export async function autoSaveDraftRemote(draftId, autoSaveJson) {
  await draftService.autoSave({
    id: Number(draftId) || draftId,
    autoSaveJson: toJsonParam(autoSaveJson),
  });
}

export async function updateDraftUndoStep(draftId, lastUndoStep) {
  await draftService.updateUndoStep({
    id: Number(draftId) || draftId,
    lastUndoStep: Number(lastUndoStep) || 0,
  });
}

export async function createDraftGroupRemote(draftId, groupName, groupElementIds) {
  const payload = await draftService.createGroup({
    draftId: Number(draftId) || draftId,
    groupName,
    groupElementIds: toJsonParam(groupElementIds),
  });
  return mapBatchGroupDto(unwrapApiResult(payload) || {});
}

export async function fetchDraftGroups(draftId) {
  const payload = await draftService.listGroups(Number(draftId) || draftId);
  return unwrapApiList(payload).map(mapBatchGroupDto);
}

export async function renameDraftGroupRemote(id, name) {
  await draftService.renameGroup({ id: Number(id) || id, name });
}

export async function deleteDraftGroupRemote(id) {
  await draftService.deleteGroup({ id: Number(id) || id });
}

function toRecordId(value) {
  if (value == null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
}

/**
 * 解析导出上限：仅 success === true 且 exceeded === true 时拦截。
 * 接口失败 / 协议不完整一律放行导出。
 */
export function parseDraftExportQuota(payload) {
  if (payload == null || typeof payload !== 'object' || payload.success !== true) {
    return {
      exceeded: false,
      checkFailed: true,
      dailyLimit: null,
      todayCount: null,
    };
  }
  const result = payload.result && typeof payload.result === 'object' ? payload.result : {};
  return {
    exceeded: result.exceeded === true,
    checkFailed: false,
    dailyLimit: result.dailyLimit ?? null,
    todayCount: result.todayCount ?? null,
  };
}

export async function checkDraftExportMaxCount() {
  try {
    const payload = await draftService.checkUploadMaxCount();
    return parseDraftExportQuota(payload);
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[draft-export] 导出上限校验失败，已放行导出', error);
    return {
      exceeded: false,
      checkFailed: true,
      dailyLimit: null,
      todayCount: null,
    };
  }
}

export function assembleDraftExportRecordBody({ draftId, taskId, fileName, fileUrl } = {}) {
  return {
    draftId: toRecordId(draftId),
    taskId: toRecordId(taskId),
    fileName: String(fileName ?? '').trim(),
    fileUrl: String(fileUrl ?? '').trim(),
  };
}

/** 导出成功后的埋点，失败不得打断导出结果 */
export async function recordDraftExport(input = {}) {
  try {
    await draftService.recordExport(assembleDraftExportRecordBody(input));
    return true;
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[draft-export] 导出记录失败', error);
    return false;
  }
}
